import { RefObject, useEffect } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard and screen-reader users land inside a dialog when it opens, stay inside it while it is
 * open, and get back to where they were when it closes. The panel itself takes the initial focus
 * (`tabIndex={-1}` on it), so the title is read first and nothing is preselected.
 */
export default function useDialogFocus(panelRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const panel = panelRef.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!panel) {
      return;
    }

    panel.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));

      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();

        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);

      if (opener?.isConnected) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [panelRef]);
}
